import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
// The "light" player has no expression evaluator, so it does not call eval() —
// required under the wasm edition's CSP (script-src 'self' 'wasm-unsafe-eval',
// no 'unsafe-eval'). Telegram .tgs stickers never use expressions anyway.
import lottie, {
  type AnimationItem,
} from 'lottie-web/build/player/lottie_light'
import { getLogger } from '@deltachat-desktop/shared/logger'

const log = getLogger('renderer/LottieSticker')

// Guards against a malicious sticker (a .tgs renders automatically when its
// message scrolls into view — no user interaction). Telegram caps .tgs at
// 64 KB; these bounds are generous but still stop a gzip bomb from expanding
// unboundedly and OOM-killing the tab (which also hosts the wasm core).
const MAX_COMPRESSED_BYTES = 1024 * 1024 // 1 MiB on the wire
const MAX_DECOMPRESSED_BYTES = 8 * 1024 * 1024 // 8 MiB after gunzip

type Props = {
  /** Ready-to-fetch blob URL of the sticker file (runtime.transformBlobURL). */
  src: string
  /** Rendered edge length in px (stickers are square-ish; SVG scales freely). */
  size: number
  /**
   * Class(es) for the container element. Defaults to the message-bubble sticker
   * classes; the composer picker passes its own so message layout doesn't leak.
   */
  className?: string
}

/**
 * Fetches a Telegram `.tgs` sticker (gzipped Lottie JSON), decompresses it, and
 * plays it with the lottie-web SVG renderer. This is the web counterpart to
 * ArcaneChat's Android LottieDecoder — the chatmail core carries the sticker
 * blob opaquely and never renders it, so the animation lives here.
 */
export default function LottieSticker({ src, size, className }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [failed, setFailed] = useState(false)
  const baseClass = className ?? 'attachment-content sticker'

  useEffect(() => {
    let animation: AnimationItem | undefined
    let cancelled = false

    loadLottieData(src)
      .then(data => {
        if (cancelled || !containerRef.current) {
          return
        }
        // Honor the OS "reduce motion" setting: still show the sticker, but as a
        // still first frame instead of a looping animation.
        const reduceMotion = window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches
        animation = lottie.loadAnimation({
          container: containerRef.current,
          renderer: 'svg',
          loop: !reduceMotion,
          autoplay: !reduceMotion,
          animationData: data,
          rendererSettings: { progressiveLoad: true },
        })
        if (reduceMotion) {
          animation.goToAndStop(0, true)
        }
      })
      .catch(err => {
        if (!cancelled) {
          log.warn('failed to render lottie sticker', err)
          setFailed(true)
        }
      })

    return () => {
      cancelled = true
      animation?.destroy()
    }
  }, [src])

  if (failed) {
    // Decompression/parse failed (corrupt sticker, or no DecompressionStream) —
    // fall back to a neutral square so the message layout stays stable.
    return (
      <div
        className={classNames(baseClass, 'lottie-sticker-fallback')}
        style={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: size / 2.5,
          opacity: 0.5,
        }}
        title='sticker could not be displayed'
      >
        🧩
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={classNames(baseClass, 'lottie-sticker')}
      style={{ width: size, height: size }}
    />
  )
}

/**
 * Fetch the sticker bytes and return parsed Lottie JSON. Sniffs the gzip magic
 * number (0x1f 0x8b) rather than trusting the extension, so both `.tgs`
 * (gzipped) and plain `.json` stickers work through the same path.
 */
async function loadLottieData(url: string): Promise<unknown> {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`sticker fetch failed: ${response.status}`)
  }
  const bytes = new Uint8Array(await response.arrayBuffer())
  if (bytes.byteLength > MAX_COMPRESSED_BYTES) {
    throw new Error(`sticker too large: ${bytes.byteLength} bytes`)
  }
  const isGzip = bytes[0] === 0x1f && bytes[1] === 0x8b
  let jsonText: string
  if (isGzip) {
    if (typeof DecompressionStream === 'undefined') {
      throw new Error('gzip decompression is not supported by this browser')
    }
    const stream = new Blob([bytes as BlobPart])
      .stream()
      .pipeThrough(new DecompressionStream('gzip'))
    jsonText = await readTextCapped(stream, MAX_DECOMPRESSED_BYTES)
  } else {
    jsonText = new TextDecoder().decode(bytes)
  }
  return JSON.parse(jsonText)
}

/**
 * Read a stream to a string, aborting once more than `cap` bytes have arrived.
 * The cap is enforced as chunks stream in, so a gzip bomb never materializes
 * its full expansion in memory.
 */
async function readTextCapped(
  stream: ReadableStream<Uint8Array>,
  cap: number
): Promise<string> {
  const reader = stream.getReader()
  const decoder = new TextDecoder()
  let text = ''
  let total = 0
  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) {
        break
      }
      total += value.byteLength
      if (total > cap) {
        throw new Error(`decompressed sticker exceeds ${cap} bytes`)
      }
      text += decoder.decode(value, { stream: true })
    }
    return text + decoder.decode()
  } finally {
    // Releases the lock; on an already-closed stream this is a no-op.
    await reader.cancel().catch(() => {})
  }
}
